import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { types as coreTypes } from '@sitebuilder/core';
import { hooks as commonHooks } from '@sitebuilder/common';
import AdminMainPanel from '../Layouts/AdminMainPanel';
import NavBar from '../Layouts/NavBar';
import { ResourcesTable, ResourcesRightSideBar } from '../BaseComponents';
import { StateShape as ReduxStateShape } from '../../reducers';
import { actionCreators, resourcesReducerActions } from '../../reducers/resources';
import { resourceActionOptions } from '../Layouts/LeftSidebar/NavOptions';
import { fileImportIconPng, fileExportIconPng } from '../../assets/images';

const SiteResourcesEditor = () => {
    const dispatch = useDispatch();
    const [pageAction, setPageAction] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {
        resources,
        resourcesLoading,
        resourcesLoadError,
        selectedResource,
        resourcesSaveError,
        resourcesSaveSuccess,
    } = useSelector((state: ReduxStateShape) => ({ ...state.resources }));

    const filteredResources: coreTypes.Resource | null = resources
        ? Object.keys(resources)
              .filter((r: string) => !['id', 'etag'].includes(r))
              .reduce((res: any, key: string) => {
                  res[key] = resources[key];
                  return res;
              }, {})
        : null;

    // resources showing up in table
    // this state is used for updates and filtering
    const [tableResources, setTableResources] = useState<coreTypes.Resource | null>(null);

    useEffect(() => {
        // set tableResources to all resources by default
        setTableResources(
            resources
                ? Object.keys(resources)
                      .filter((r: string) => !['id', 'etag'].includes(r))
                      .reduce((res: any, key: string) => {
                          res[key] = resources[key];
                          return res;
                      }, {})
                : null
        );
    }, [resources]);

    const i18n = commonHooks.useLocalization();

    useEffect(() => {
        dispatch(actionCreators.getResources());
    }, [dispatch]);

    useEffect(() => {
        if (selectedResource && selectedResource.id) {
            setPageAction('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedResource]);

    const handleSaveResources = () => {
        // merge updated tableResources if any with site resources
        dispatch(
            actionCreators.saveResources(_.merge({}, resources, tableResources), 'site', false)
        );
    };

    // Function to handle file upload
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        console.log(!file);
        if (!file) return;

        if (file.type !== 'application/json') {
            dispatch(actionCreators.setResourcesSaveError('Only JSON files are allowed.'));
            return;
        }

        try {
            const fileData = new FormData();
            fileData.append('file', file);
            dispatch(actionCreators.importResources(fileData));
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    const handleExport = () => {
        const jsonString = JSON.stringify(resources, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'resources';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="resources">
            <AdminMainPanel>
                <NavBar
                    rightSidebar={
                        <ResourcesRightSideBar
                            resourcesType={'site'}
                            addNewLocale={pageAction === 'locale'}
                            resources={resources}
                            filteredResources={filteredResources}
                            addNewResource={pageAction === 'resource'}
                        />
                    }
                    hideRightSidebar={!pageAction}
                />
                <div className="admin-main-content">
                    <div
                        className={`title-and-buttons-container ${!pageAction ? '' : 'collapsed'}`}
                    >
                        <h3 className="title">{i18n.t('resources_title')}</h3>
                        <div className="actions-container">
                            <div className="action-buttons-container">
                                {resourceActionOptions.map((o) => (
                                    <div
                                        onClick={() => {
                                            setPageAction(pageAction === o.name ? '' : o.name);
                                            dispatch(actionCreators.setSelectedResource(null));
                                            dispatch(
                                                resourcesReducerActions.RESOURCES_SAVE_END({
                                                    success: '',
                                                })
                                            );
                                        }}
                                        key={o.name}
                                    >
                                        <img
                                            src={pageAction === o.name ? o.activeIcon : o.icon}
                                            alt={o.name}
                                            className={'main-left-sidebar-icon icon'}
                                        />
                                    </div>
                                ))}
                                <div onClick={triggerFileInput}>
                                    <img
                                        src={fileImportIconPng}
                                        alt={'Import Resources'}
                                        className={'main-left-sidebar-icon icon import'}
                                    />
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        accept="application/json"
                                        onChange={handleFileUpload}
                                    />
                                </div>
                                <div onClick={handleExport}>
                                    <img
                                        src={fileExportIconPng}
                                        alt={'Export Resources'}
                                        className={'main-left-sidebar-icon icon import'}
                                    />
                                </div>

                                <div className="button-container">
                                    <button
                                        className="primary-button save-button btn btn-secondary"
                                        onClick={handleSaveResources}
                                    >
                                        {i18n.t('save')}
                                    </button>
                                </div>
                            </div>
                            <div className="error-success-msg">
                                <p className="error-msg">{resourcesSaveError}</p>
                                <p className="success-msg">{resourcesSaveSuccess}</p>
                            </div>
                        </div>
                    </div>
                    <ResourcesTable
                        resources={filteredResources}
                        resourcesLoading={resourcesLoading}
                        resourcesLoadError={resourcesLoadError}
                        resourcesType={'site'}
                        fullWidth={!pageAction}
                        tableResources={tableResources}
                        setTableResources={setTableResources}
                    />
                </div>
            </AdminMainPanel>
        </div>
    );
};

export default SiteResourcesEditor;
